import React, { useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, RefreshCw, Ban } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PageHeader } from "@shared/components/PageHeader";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import CreateReworkOrderModal from "./view/reworkOrderModal";

import { useRejectedSO, useRejectedSummary } from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";
import { RejectionListFilters } from "./filterPanel";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";

interface RejectionListItem {
  id: string;
  soId: string;
  soName: string;
  originalTechnician: string;
  rejectionReason: string;
  rejectedOn: string;
  status: string;
  reworkSO?: string;
  priority: string;
  rejectedBy: string;
  rejectionComments?: string;
  serviceOrderData?: any;
}

interface RejectionFilters {
  status?: string;
}

const RejectionList: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRejection, setSelectedRejection] = useState<RejectionListItem | null>(null);

  // Parse params from URL
  const searchQuery = searchParams.get("query") || "";
  const statusFilter = searchParams.get("status") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");
  const pageLimit = PAGE_SIZE;

  // Build current filters state
  const currentFilters: RejectionFilters = useMemo(() => ({
    status: statusFilter || undefined,
  }), [statusFilter]);

  const { data: summaryData } = useRejectedSummary({ remote_utility_id: remoteUtilityId });

  // Build API query params
  const queryParams = useMemo(() => ({
    remote_utility_id: remoteUtilityId,
    is_rejected: "1",
    limit: pageLimit,
    page: currentPage,
    ...(searchQuery && { search_data: searchQuery }),
    ...(statusFilter && { status: statusFilter }),
  }), [remoteUtilityId, searchQuery, currentPage, pageLimit, statusFilter]);

  // Fetch data
  const { data: rejectedSOData, isLoading, refetch } = useRejectedSO(queryParams);

  const rejectionData = rejectedSOData?.results || [];

  // Transform API pagination to GenericTable format
  const pagination = useMemo(() => {
    if (!rejectedSOData) return undefined;
    
    const totalItems = rejectedSOData.count || 0;

    if (totalItems === 0) return undefined;

    const totalPages = Math.ceil(totalItems / pageLimit);
    const startItem = (currentPage - 1) * pageLimit + 1;
    const endItem = Math.min(currentPage * pageLimit, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: pageLimit,
      startItem,
      endItem,
    };
  }, [rejectedSOData, currentPage, pageLimit]);

  // KPI cards
  const rejectionKpiCards = [
    {
      title: "Total Rejected Orders",
      count: summaryData?.result?.totalRejected || 0,
      trendSummary: " from yesterday",
      trendDirection: "down" as const,
      icon: <Ban className="h-8 w-8 text-red-500" />,
    },
    {
      title: "Awaiting Rework Assignment",
      count: summaryData?.result?.awaitingReworkAssignment || 0,
      trendSummary: "Needs immediate attention",
      trendDirection: "down" as const,
      icon: <Clock className="h-8 w-8 text-orange-500" />,
    },
    {
      title: "Rework In Progress",
      count: summaryData?.result?.reworkInProgress || 0,
      trendSummary: "Currently being worked on",
      trendDirection: "up" as const,
      icon: <RefreshCw className="h-8 w-8 text-blue-500" />,
    },
    {
      title: "Breaching SLA",
      count: summaryData?.result?.breachingSla || 0,
      trendSummary: "Requires urgent action",
      trendDirection: "down" as const,
      icon: <AlertTriangle className="h-8 w-8 text-red-500" />,
    },
  ];

  // Event Handlers
  const handleSearch = (query: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (query) {
      newParams.set("query", query);
    } else {
      newParams.delete("query");
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
  };

  const handleApplyFilters = (filters: Partial<RejectionFilters>) => {
    const newParams = new URLSearchParams(searchParams);
    
    // Apply status filter
    if (filters.status) {
      newParams.set("status", filters.status);
    } else {
      newParams.delete("status");
    }
    
    // Reset to page 1
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("status");
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const onViewRejectionDetails = (row: RejectionListItem) => {
    navigate(`/service-orders/dispatcher/rejected-service-order/${row.id}`);
  };

  const onCreateReworkOrder = async (row: RejectionListItem) => {
    setSelectedRejection(row);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRejection(null);
    refetch();
  };

  // Table Configuration
  const columns: TableColumn<RejectionListItem>[] = [
    {
      key: "jobCardNo",
      header: "SO ID",
      width: "w-32",
    },
    {
      key: "sopName",
      header: "SO Name",
      width: "w-64",
    },
    {
      key: "fieldForceName",
      header: "Original Technician",
      width: "w-48",
    },
    {
      key: "rejectionReasonDisplay",
      header: "Rejection Reason",
      width: "w-64",
      render: (value) => (
        <span title={value}>
          {value && value.length > 15 ? `${value.substring(0, 15)}...` : value}
        </span>
      ),
    },
    {
      key: "rejectedDate",
      header: "Rejected On",
      width: "w-32",
    },
 {
  key: "statusDisplay",
  header: "Status",
  width: "w-32",
  render: (value) => (
    <span
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium border ${getStatusBadgeClass(value || "")}`}
    >
      {value}
    </span>
  ),
},
    {
      key: "reworkOrders",
      header: "Rework SO",
      width: "w-32",
      render: (value: any[], row: any) => {
        if (!value || value.length === 0) return "-";
        return value.map((reworkOrder) => (
          <span
            key={reworkOrder.id}
            className="text-blue-600 cursor-pointer hover:underline block"
            onClick={() => navigate(`/service-orders/dispatcher/rejected-service-order/${reworkOrder.id}`)}
          >
            {reworkOrder.jobCardNo}
          </span>
        ));
      },
    },
  ];

  const tableActions = [
    {
      label: "View Rejection Details",
      onClick: (row: any) => onViewRejectionDetails(row),
      icon: "Eye" as const,
      variant: "ghost" as const,
    },
    {
      label: "Create Rework Order",
      onClick: (row: any) => onCreateReworkOrder(row),
      icon: "FileText" as const,
      disabled: (row: any) => row.statusDisplay === "Rework Assigned",
    },
  ];

  return (
    <div>
      <div className="space-y-4">
        <PageHeader
          title="Rejected Service Orders"
          subtitle="Manage and reassign rejected service orders"
        />
        
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {rejectionKpiCards.map((card, index) => (
            <QuickStatsCard
              key={index}
              title={card.title}
              count={card.count}
              trendSummary={card.trendSummary}
              trendDirection={card.trendDirection}
              icon={card.icon}
            />
          ))}
        </div>

        <GenericTable
          data={rejectionData}
          columns={columns}
          actions={tableActions}
          rowKey="id"
          loading={isLoading}
          onSearch={handleSearch}
          searchPlaceholder="Search by SO ID, Name, Technician..."
          searchParamName="query"
          searchDebounceMs={300}
          pagination={pagination}
          onPageChange={handlePageChange}
          filterPanel={
            <RejectionListFilters
              currentFilters={currentFilters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
            />
          }
          emptyMessage="No rejections found matching your criteria"
        />
      </div>

      {selectedRejection && (
        <CreateReworkOrderModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          parentOrder={{
            id: selectedRejection.id,
            name: selectedRejection.soName,
            rejectionReason: selectedRejection.rejectionReason,
            rejectionComments: selectedRejection.rejectionComments,
            rejectedBy: selectedRejection.rejectedBy,
            serviceOrderData: selectedRejection,
          }}
        />
      )}
    </div>
  );
};

export default RejectionList;